import React, { useMemo } from 'react';
import { utcToLocal, localToUTC } from '@rjsf/utils';
import { useJnx, type JnxExpr } from 'src/util/jnx';
import { useSideChannelSubscription, type SideChannel } from 'src/util/useSideChannel';

// `.cf-DateTimeWidget` — themable tokens via CSS (see tailwind.css).
const INPUT_CLASS =
  'cf-DateTimeWidget w-full theme-text-primary focus:outline-none focus:ring-2 focus:ring-emerald-800/40 focus:border-blue-500/50 transition-all';

interface DateTimeWidgetProps {
  id: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  autofocus?: boolean;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  formContext: { sideChannel: SideChannel<unknown[]> };
  uiSchema?: {
    'ui:disableIf'?: JnxExpr;
    'ui:placeHolder'?: string;
    'ui:classNames'?: string;
  };
}

/**
 * Datetime widget. Renders a native datetime-local input wrapped in the same
 * `mb-3 space-y-1` container used by TextInput so vertical rhythm matches the
 * rest of the form fields. Converts UTC ⇄ local on read/write.
 */
export default function DateTimeWidget(props: DateTimeWidgetProps) {
  const {
    id,
    required,
    disabled,
    readonly,
    autofocus,
    value,
    onChange,
    formContext,
    uiSchema: { 'ui:disableIf': uiDisableIf, 'ui:placeHolder': placeholder, 'ui:classNames': classNames } = {},
  } = props;

  const rootFormData = useSideChannelSubscription(formContext?.sideChannel, 0);

  const disableIfJnx = useJnx(uiDisableIf);
  const disableIf = useMemo(
    () =>
      disableIfJnx?.eval(rootFormData || {}, '', {
        rootData: rootFormData,
        formContext,
      }),
    [disableIfJnx, rootFormData, formContext]
  );

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    onChange(e.target.value ? localToUTC(e.target.value) : undefined);
  }

  return (
    <div className="space-y-1">
      <input
        type="datetime-local"
        id={id}
        className={`${INPUT_CLASS} ${classNames || ''}`}
        value={utcToLocal(value ?? '') || ''}
        placeholder={placeholder || ''}
        required={required}
        disabled={disabled || disableIf}
        readOnly={readonly}
        autoFocus={autofocus}
        onChange={handleChange}
      />
    </div>
  );
}
